import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonIcon from '@mui/icons-material/Person';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { Link as RouterLink } from 'react-router-dom';
import useIsMobile from '../../../hooks/useIsMobile';
import useSessionContext from '../../../data/queryHooks/useSessionContext';
import {
	getHeaderNavs,
	getProfileNavs,
	getCurrentPermission,
	getMessageCenterPath,
} from '../../../scripts/navigationHelpers';
import {
	pageKey,
	retrievePermissionWithKey,
} from '../../../scripts/pageConstants';
import MobileAccountMenu from './MobileAccountMenu';
import MobileNavMenu from './MobileNavMenu';
import NavigationBar from './NavigationBar';
import HomeLink from './HomeLink';
import UserInfoHeader from './UserInfoHeader';

const Header = ({ hasDefaultLocation, ...props }) => {
	const isMobile = useIsMobile();
	const [anchorEl, setAnchorEl] = useState(null);
	const isProfileMenuOpen = Boolean(anchorEl);
	const profileBtnId = 'profile-btn';
	const profileMenuId = 'profile-menu';
	const { permissions, redirectionLinks, userInfo } = useSessionContext();
	const onProfileBtnClick = event => {
		setAnchorEl(event.currentTarget);
	};
	const { palette } = useTheme();

	const handleClose = () => {
		setAnchorEl(null);
	};

	const [navigationItems, setNavigationItems] = useState(
		getHeaderNavs(permissions, redirectionLinks)
	);
	const profileItems = getProfileNavs(permissions, redirectionLinks);

	useEffect(() => {
		// This is to address the bug: 534625
		if (userInfo?.clubTypeID === 300 || userInfo?.clubTypeID === 301) {
			// grab the items from billing & payments dropdown so that we can copy it over to the member management dropdown
			let billingDropdownList = [];
			const updatedList = [...navigationItems];

			const billingIndex = updatedList.findIndex(item => {
				if (item?.key === 'billing-and-payments') {
					billingDropdownList = item?.menuItems;
					return true;
				}
				return false;
			});

			// remove billing and payments array so the dropdown doesn't display
			if (billingIndex) {
				updatedList.splice(billingIndex, 1);
			}

			setNavigationItems(
				updatedList.map(item => {
					if (item.key === 'member-management') {
						return {
							...item,
							menuItems: [
								...item.menuItems,
								// add the items from billing dropdown to member management dropdown
								...billingDropdownList,
							],
						};
					}
					return item;
				})
			);
		}
	}, []);

	const hasMessageCenterPermission = () => {
		if (permissions) {
			return !!getCurrentPermission(
				retrievePermissionWithKey[pageKey.messageCenter],
				permissions
			);
		}
		return false;
	};
	const renderMenuItem = menuItem => (
		<MenuItem
			key={menuItem.key}
			{...(menuItem.isLegacy
				? {
						component: Link,
						href: menuItem.path,
						target: '_self',
						rel: 'noreferrer',
					}
				: {
						component: RouterLink,
						to: menuItem.path,
					})}
		>
			{menuItem.label}
		</MenuItem>
	);

	return (
		<AppBar
			{...props}
			position='static'
			sx={{
				backgroundColor: palette.common.white,
			}}
		>
			<Box
				sx={{
					width: '100%',
					backgroundColor: 'primary.dark',
				}}
			>
				<Box
					sx={{
						px: { xs: 1, sm: 1 },
						maxWidth: '100%',
					}}
				>
					<Toolbar
						sx={{
							justifyContent: 'space-between',
							py: 0,
							px: { xs: 0, sm: 0, md: 4 },
							minHeight: { xs: '64px', sm: '64px' },
							height: { xs: '64px', md: '80px' },
						}}
					>
						<Stack direction='row'>
							{isMobile && hasDefaultLocation && (
								<MobileNavMenu
									navigationItems={navigationItems}
								/>
							)}
							<HomeLink hasDefaultLocation={hasDefaultLocation} />
						</Stack>

						<Stack direction='row' spacing={1}>
							{hasMessageCenterPermission() &&
							hasDefaultLocation ? (
								<IconButton
									component={Link}
									href={getMessageCenterPath(
										redirectionLinks
									)}
									target='_self'
									rel='noreferrer'
									aria-label='message center'
								>
									<MailOutlineIcon
										sx={{ color: palette.common.white }}
									/>
								</IconButton>
							) : null}

							{isMobile && (
								<MobileAccountMenu
									profileItems={profileItems}
									hasDefaultLocation={hasDefaultLocation}
								/>
							)}
							{!isMobile && (
								<>
									<IconButton
										id={profileBtnId}
										aria-controls={
											isProfileMenuOpen
												? profileMenuId
												: undefined
										}
										aria-haspopup='true'
										aria-expanded={
											isProfileMenuOpen
												? 'true'
												: undefined
										}
										aria-label='profile menu'
										data-testid='profile-menu'
										onClick={onProfileBtnClick}
									>
										<PersonIcon
											sx={{ color: palette.common.white }}
										/>
									</IconButton>
									<Menu
										id={profileMenuId}
										anchorEl={anchorEl}
										open={isProfileMenuOpen}
										onClose={handleClose}
										MenuListProps={{
											'aria-labelledby': profileBtnId,
										}}
									>
										{profileItems.map(menuItem => {
											if (
												!hasDefaultLocation &&
												menuItem?.key === 'log-out'
											) {
												return renderMenuItem(menuItem);
											}

											if (hasDefaultLocation) {
												return renderMenuItem(menuItem);
											}
											return null;
										})}
									</Menu>
								</>
							)}
						</Stack>
					</Toolbar>
				</Box>
			</Box>
			{!isMobile && hasDefaultLocation && (
				<NavigationBar items={navigationItems} />
			)}
			{!isMobile && (
				<UserInfoHeader hasDefaultLocation={hasDefaultLocation} />
			)}
		</AppBar>
	);
};

export default Header;

Header.propTypes = {
	hasDefaultLocation: PropTypes.bool.isRequired,
};
